import type { HomeTile as Tile } from '@str/api/types';
import type { Channel } from '@str/shared';
import { deltaLabel, splitLabel } from '@/lib/home';
import { compact } from '@/lib/format';
import { Sparkline } from './Sparkline';
import { useCountUp } from './motion';

function Big({ value, dash }: { value: number; dash: boolean }) {
  const shown = useCountUp(value);
  return <span className="h-tile-n">{dash ? '—' : shown.toLocaleString('en-US')}</span>;
}

export function HomeTile({
  label,
  tile,
  period,
  live,
  notIn,
  waiting = [],
  delay,
}: {
  label: string;
  tile: Tile;
  period: number;
  /** Clicks: ours, counted as they happen; the tile says so instead of a delta when there is nothing to compare. */
  live?: boolean;
  /** While no channel has reported: "still counting" inside the window, else why not. */
  notIn: { mark: string; detail: string | null };
  /** Channels that cannot report yet: named in the split, never counted as zero. */
  waiting?: Channel[];
  delay: number;
}) {
  const delta = deltaLabel(tile.deltaPct);
  // Clicks are ours and never wait on a platform.
  const shownWaiting = live ? [] : waiting;
  return (
    <div
      role="group"
      aria-label={label}
      className="h-tile h-rise"
      style={{ animationDelay: `${delay}ms` }}
    >
      <span className="h-tile-l">{label}</span>
      <span className="h-tile-d">
        {tile.counting ? (
          <>
            <span className="h-mark">{notIn.mark}</span>
            {notIn.detail && <span> · {notIn.detail}</span>}
          </>
        ) : delta ? (
          <>
            <span className={tile.deltaPct! >= 0 ? 'h-up' : 'h-down'}>
              <span aria-hidden="true">{tile.deltaPct! >= 0 ? '▲' : '▼'} </span>
              <span>{delta}</span>
            </span>
            <span> vs the {period} days before</span>
          </>
        ) : live ? (
          <>
            <span className="h-mark">live</span>
            <span> · counted as they happen</span>
          </>
        ) : (
          <span>nothing to compare with yet</span>
        )}
      </span>
      <Big key={period} value={tile.value} dash={tile.counting && tile.value === 0} />
      <Sparkline series={tile.series} soft={tile.value === 0} />
      {(tile.split.length > 0 || shownWaiting.length > 0) && (
        <span className="h-split">
          {tile.split.map((s, i) => (
            <span key={s.channel}>
              {i > 0 && <span className="h-dot"> · </span>}
              <span>{splitLabel(s.channel)}</span> <b>{compact(s.value)}</b>
            </span>
          ))}
          {shownWaiting.map((c, i) => (
            <span key={`wait-${c}`}>
              {(i > 0 || tile.split.length > 0) && <span className="h-dot"> · </span>}
              <span>{`${splitLabel(c)} not available yet`}</span>
            </span>
          ))}
        </span>
      )}
    </div>
  );
}
